import { useCallback, useEffect, useState } from 'react';
import type { AuditRecord, QaRunRecord } from '@360t/shared';
import { api, ApiError } from '../lib/api';
import { useJobPolling } from '../lib/usePolling';
import { IssueList, auditIssuesToEntries, qaDefectsToEntries } from '../components/IssueList';
import { ProgressBar } from '../components/ProgressBar';
import { ScoreGauge } from '../components/ScoreGauge';

type Mode = 'audit' | 'qa';

export function ScanPage() {
  const [url, setUrl] = useState('');
  const [mode, setMode] = useState<Mode>('audit');
  const [strategy, setStrategy] = useState<'mobile' | 'desktop'>('mobile');
  const [maxPages, setMaxPages] = useState(10);
  const [submitForms, setSubmitForms] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [auditId, setAuditId] = useState<string | null>(null);
  const [qaRunId, setQaRunId] = useState<string | null>(null);
  const [pageSpeedConfigured, setPageSpeedConfigured] = useState(true);

  const fetchAudit = useCallback((id: string) => api.getAudit(id), []);
  const fetchQa = useCallback((id: string) => api.getQaRun(id), []);
  const audit = useJobPolling<AuditRecord>(auditId, fetchAudit);
  const qa = useJobPolling<QaRunRecord>(qaRunId, fetchQa);

  useEffect(() => {
    void api
      .health()
      .then((h) => setPageSpeedConfigured(Boolean(h.features?.pageSpeed)))
      .catch(() => undefined);
  }, [audit.data?.status, qa.data?.status]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setFormError(null);
    setSubmitting(true);
    try {
      if (mode === 'audit') {
        setQaRunId(null);
        const job = await api.startAudit({ url, strategy, generatePdf: true });
        setAuditId(job.id);
      } else {
        setAuditId(null);
        const job = await api.startQa({ url, maxPages, submitForms, generatePdf: true });
        setQaRunId(job.id);
      }
    } catch (err) {
      const message =
        err instanceof ApiError && err.details
          ? `${err.message}: ${JSON.stringify((err.details as any).fieldErrors ?? err.details)}`
          : (err as Error).message;
      setFormError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const running = audit.polling || qa.polling;

  return (
    <div className="mx-auto max-w-5xl px-6 py-10">
      {/* ── scan form ── */}
      <form onSubmit={submit} className="card mb-8">
        <div className="mb-4 inline-flex rounded-lg border border-line bg-canvas p-1">
          {(['audit', 'qa'] as Mode[]).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              className={`rounded-md px-4 py-1.5 text-sm font-semibold transition ${
                mode === m ? 'bg-white text-ink shadow-sm' : 'text-slate-500 hover:text-ink'
              }`}
            >
              {m === 'audit' ? 'Website audit' : 'QA scan'}
            </button>
          ))}
        </div>

        <label className="label" htmlFor="url">
          Website URL
        </label>
        <div className="flex flex-col gap-3 sm:flex-row">
          <input
            id="url"
            className="input"
            placeholder="example.com"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            required
          />
          <button type="submit" className="btn-primary whitespace-nowrap" disabled={submitting || running}>
            {submitting ? 'Queueing…' : running ? 'Running…' : mode === 'audit' ? 'Run audit' : 'Run QA scan'}
          </button>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-6 text-sm">
          {mode === 'audit' ? (
            <label className="flex items-center gap-2 text-slate-600">
              Test as
              <select
                className="rounded-md border border-line px-2 py-1 text-sm"
                value={strategy}
                onChange={(e) => setStrategy(e.target.value as 'mobile' | 'desktop')}
              >
                <option value="mobile">Mobile</option>
                <option value="desktop">Desktop</option>
              </select>
            </label>
          ) : (
            <>
              <label className="flex items-center gap-2 text-slate-600">
                Max pages
                <input
                  type="number"
                  min={1}
                  max={100}
                  className="w-20 rounded-md border border-line px-2 py-1 text-sm"
                  value={maxPages}
                  onChange={(e) => setMaxPages(Number(e.target.value))}
                />
              </label>
              <label className="flex items-center gap-2 text-slate-600">
                <input type="checkbox" checked={submitForms} onChange={(e) => setSubmitForms(e.target.checked)} />
                Test form submissions
                <span className="text-xs text-slate-400">(payment &amp; login forms are always skipped)</span>
              </label>
            </>
          )}
        </div>

        {mode === 'audit' && !pageSpeedConfigured && (
          <p className="mt-4 rounded-lg bg-average/10 px-3 py-2 text-xs text-amber-700">
            PAGESPEED_API_KEY is not set — this audit will run without performance scores.
          </p>
        )}
        {formError && <p className="mt-4 rounded-lg bg-poor/10 px-3 py-2 text-sm text-poor">{formError}</p>}
      </form>

      {/* ── audit result ── */}
      {audit.data && (
        <section className="card mb-6">
          <div className="mb-5 flex items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-bold text-ink">Website audit</h2>
              <p className="text-sm text-slate-500">{audit.data.url}</p>
            </div>
            {audit.data.pdfPath && (
              <a href={audit.data.pdfPath} target="_blank" rel="noreferrer" className="btn-ghost">
                Download PDF report
              </a>
            )}
          </div>

          {audit.data.status !== 'completed' && audit.data.status !== 'failed' && (
            <ProgressBar percent={audit.data.progress} label={audit.data.progressLabel} />
          )}
          {audit.data.status === 'failed' && (
            <p className="rounded-lg bg-poor/10 px-4 py-3 text-sm text-poor">{audit.data.error}</p>
          )}

          {audit.data.status === 'completed' && (
            <>
              <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-5">
                <ScoreGauge score={audit.data.overallScore} label="Overall" />
                <ScoreGauge score={audit.data.performanceScore} label="Performance" />
                <ScoreGauge score={audit.data.seoScore} label="SEO" />
                <ScoreGauge score={audit.data.aeoScore} label="AEO" />
                <ScoreGauge score={audit.data.accessibilityScore} label="Accessibility" />
              </div>
              <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-slate-500">
                Issues ({audit.data.issues.length})
              </h3>
              <IssueList
                entries={auditIssuesToEntries(audit.data.issues)}
                emptyText="No issues found — this site is in excellent shape."
              />
            </>
          )}
        </section>
      )}

      {/* ── QA result ── */}
      {qa.data && (
        <section className="card">
          <div className="mb-5 flex items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-bold text-ink">Automated QA scan</h2>
              <p className="text-sm text-slate-500">{qa.data.url}</p>
            </div>
            {qa.data.pdfPath && (
              <a href={qa.data.pdfPath} target="_blank" rel="noreferrer" className="btn-ghost">
                Download QA report
              </a>
            )}
          </div>

          {qa.data.status !== 'completed' && qa.data.status !== 'failed' && (
            <ProgressBar percent={qa.data.progress} label={qa.data.progressLabel} />
          )}
          {qa.data.status === 'failed' && (
            <p className="rounded-lg bg-poor/10 px-4 py-3 text-sm text-poor">{qa.data.error}</p>
          )}

          {qa.data.status === 'completed' && (
            <>
              <div className="mb-6 flex flex-wrap items-center gap-8">
                <ScoreGauge score={qa.data.qaScore} label="QA health" size={140} />
                <div className="grid flex-1 grid-cols-2 gap-3 sm:grid-cols-4">
                  {[
                    { label: 'Pages tested', value: qa.data.result?.pagesCrawled.length ?? 0 },
                    { label: 'Broken links', value: qa.data.result?.summary.brokenLinks ?? 0 },
                    { label: 'JS errors', value: qa.data.result?.summary.consoleErrors ?? 0 },
                    { label: 'WCAG issues', value: qa.data.result?.summary.a11yViolations ?? 0 },
                  ].map((s) => (
                    <div key={s.label} className="rounded-lg border border-line px-3 py-2">
                      <div className="text-lg font-extrabold text-ink">{s.value}</div>
                      <div className="text-[11px] uppercase tracking-wide text-slate-400">{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>

              {qa.data.result && (
                <div className="mb-6 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-line text-left text-[11px] uppercase tracking-wide text-slate-400">
                        <th className="pb-2">Category</th>
                        <th className="pb-2">Result</th>
                        <th className="pb-2">Score</th>
                        <th className="pb-2">Defects</th>
                      </tr>
                    </thead>
                    <tbody>
                      {qa.data.result.categories.map((c) => (
                        <tr key={c.category} className="border-b border-line last:border-0">
                          <td className="py-2 font-medium capitalize text-ink">{c.category.replace('-', ' ')}</td>
                          <td className="py-2">
                            {c.skippedReason ? (
                              <span className="text-slate-400">N/A</span>
                            ) : c.passed ? (
                              <span className="font-semibold text-good">Pass</span>
                            ) : (
                              <span className="font-semibold text-poor">Fail</span>
                            )}
                          </td>
                          <td className="py-2">{c.skippedReason ? '—' : `${c.score}/100`}</td>
                          <td className="py-2">{c.defectCount}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-slate-500">
                Defects ({qa.data.defects.length})
              </h3>
              <IssueList
                entries={qaDefectsToEntries(qa.data.defects)}
                emptyText="No defects found — everything we tested works."
              />
            </>
          )}
        </section>
      )}
    </div>
  );
}
